import { useMemo } from "react";

import { BookingRowCard } from "./BookingRowCard";
import { BookingsDaySectionHeader } from "./BookingsDaySectionHeader";
import { buildOrderedDaySections } from "./bookingCalendar";
import { totalGuests } from "./bookingDayAgenda";
import type { BookingListRow } from "./bookingViewModel";

interface BookingsPartnerListProps {
  /** One server page, already ordered by visit. */
  rows: BookingListRow[];
  bookingsListPath: string;
}

/** Partner list page grouped into activity-local visit days. */
export function BookingsPartnerList({
  rows,
  bookingsListPath,
}: BookingsPartnerListProps) {
  const pageSections = useMemo(
    () => buildOrderedDaySections(rows, { pastFirst: false }),
    [rows],
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="-mx-4 space-y-5 sm:mx-0 sm:space-y-6">
      {pageSections.map((section) => {
        const covers = totalGuests(section.bookings);

        return (
          <section
            key={section.dateKey ?? "undated"}
            className="space-y-2.5"
          >
            <BookingsDaySectionHeader
              dateKey={section.dateKey}
              bookingCount={section.bookings.length}
              covers={covers}
              idPrefix="partner-bookings-day"
              className="px-4 sm:px-0"
            />

            <div className="flex flex-col gap-px bg-surface-200 sm:gap-2 sm:bg-transparent">
              {section.bookings.map((booking) => (
                <BookingRowCard
                  key={`${booking.type}-${booking.id}`}
                  booking={booking}
                  detailPath={`${bookingsListPath}/${booking.id}`}
                  omitVisitDate={section.dateKey != null}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
